import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FullScreenLayout } from '../../routes/layouts/FullScreenLayout';
import { FullScreenHeader } from '@/components/shared/layout';
import { useAuth } from '../../contexts/AuthContext';
import { useClassDetail } from '@/data/queries/useClasses';
import { useClassEvaluation } from '@/data/queries/useEvaluation';
import { classDetailDtoToClassItem } from '@/utils/classUtils';
import { getScenarioBasicInfo } from '@/services/scenarioService';
import { Top3Winners, type WinnerEntry } from '../results/Top3Winners';

type TabType = 'curriculum' | 'evaluation' | 'practice-status' | 'ranking' | 'statistics';

export default function PracticeRanking(): React.ReactElement {
    const { classId: classIdParam } = useParams<{ classId: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { getCurrentRole } = useAuth();

    const role = getCurrentRole();
    const basePath = role === 'master' ? '/master' : '/admin';
    const classId = parseInt((classIdParam ?? '').trim(), 10);
    const currentTab = searchParams.get('tab') || 'ranking';

    const [selectedScenarioId, setSelectedScenarioId] = useState<number | null>(null);
    const [scenarioContentCode, setScenarioContentCode] = useState<string | null>(null);
    const [scenarioModeValue, setScenarioModeValue] = useState<string | null>(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const {
        data: classDetailDto,
        isError: isClassError,
        isLoading: isClassLoading,
    } = useClassDetail(classId, {
        enabled: classId > 0,
    });

    const classItem = classDetailDto ? classDetailDtoToClassItem(classDetailDto) : null;

    // 커리큘럼에서 시나리오 목록 추출
    const scenarios = useMemo(() => {
        if (!classItem?.curriculum) return [];
        
        return classItem.curriculum
            .filter((item) => item.type === 'scenario')
            .map(item => ({
                id: item.id,
                name: item.name,
                scenarioId: item.scenarioId,
                practiceMode: item.practiceMode,
            }));
    }, [classItem]);

    // 첫 번째 시나리오를 기본 선택
    React.useEffect(() => {
        if (scenarios.length > 0 && selectedScenarioId === null) {
            setSelectedScenarioId(scenarios[0].id);
        }
    }, [scenarios, selectedScenarioId]);

    const selectedScenario = useMemo(() => {
        return scenarios.find(s => s.id === selectedScenarioId) || null;
    }, [scenarios, selectedScenarioId]);

    // 시나리오 선택 시 상세 정보 조회하여 contentCode(key)와 mode(modeValue) 가져오기
    React.useEffect(() => {
        const scenarioId = selectedScenario?.scenarioId;
        if (!scenarioId) {
            setScenarioContentCode(null);
            setScenarioModeValue(null);
            return;
        }

        const fetchScenarioKey = async () => {
            try {
                const scenarioInfo = await getScenarioBasicInfo(
                    scenarioId,
                    'ko',
                    String(classId)
                );
                const key = (scenarioInfo as any)?.key || null;
                const modeValue = (scenarioInfo as any)?.modeValue || null;
                setScenarioContentCode(key);
                setScenarioModeValue(modeValue);
            } catch (error) {
                console.error('Failed to fetch scenario info:', error);
                setScenarioContentCode(null);
                setScenarioModeValue(null);
            }
        };

        fetchScenarioKey();
    }, [selectedScenario, classId]);

    // 평가결과 API 호출 (날짜 필터 포함)
    const {
        data: evaluationData,
        isLoading: isEvaluationLoading,
        isError: isEvaluationError,
    } = useClassEvaluation(
        classId,
        {
            contentCode: scenarioContentCode || undefined,
            mode: scenarioModeValue || undefined,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            sortBy: 'highest_score',
            sortOrder: false, // 내림차순
        },
        {
            enabled: classId > 0 && !!scenarioContentCode,
        }
    );

    const backToClassDetailPath = (): string => {
        return `${basePath}/class-management/${classId}`;
    };

    const handleTabChange = (tab: TabType) => {
        if (tab === 'curriculum') {
            if (classItem?.curriculum?.length) {
                const firstSession = classItem.curriculum[0];
                navigate(`${basePath}/class-management/${classId}/curriculum/${firstSession.id}`);
            }
        } else if (tab === 'evaluation') {
            navigate(`${basePath}/class-management/${classId}/evaluation`);
        } else if (tab === 'practice-status') {
            navigate(`${basePath}/class-management/${classId}/practice-status`);
        } else if (tab === 'ranking') {
            return;
        } else if (tab === 'statistics') {
            navigate(`${basePath}/class-management/${classId}/statistics`);
        } else {
            navigate(`${basePath}/class-management/${classId}/session-management?tab=${tab}`);
        }
    };

    const tabs = [
        { id: 'curriculum', label: '커리큘럼' },
        { id: 'evaluation', label: '평가결과' },
        { id: 'practice-status', label: '실습현황' },
        { id: 'ranking', label: '랭킹' },
        { id: 'statistics', label: '실습통계' },
    ];

    const renderCenterContent = () => {
        return (
            <div className="flex gap-2">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => handleTabChange(tab.id as TabType)}
                        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            currentTab === tab.id
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
        );
    };

    const renderRightContent = () => {
        return (
            <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600">조회기간</span>
                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded text-xs w-32"
                />
                <span className="text-gray-600">~</span>
                <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded text-xs w-32"
                />
            </div>
        );
    };

    // Top 3 추출 (highest_score 기준 내림차순)
    const top3Winners: WinnerEntry[] = useMemo(() => {
        // 기본 빈 슬롯 3개 생성
        const emptySlots: WinnerEntry[] = [
            { rank: 1, nickname: '—', email: '—', score: 0 },
            { rank: 2, nickname: '—', email: '—', score: 0 },
            { rank: 3, nickname: '—', email: '—', score: 0 },
        ];

        if (!evaluationData?.userList || evaluationData.userList.length === 0) {
            return emptySlots;
        }

        // highest_score 기준으로 정렬 (내림차순)
        const sortedUsers = [...evaluationData.userList]
            .filter(user => user.highest_score !== null && user.highest_score > 0)
            .sort((a, b) => (b.highest_score || 0) - (a.highest_score || 0))
            .slice(0, 3);

        // 실제 유저 데이터로 채우기
        sortedUsers.forEach((user, index) => {
            emptySlots[index] = {
                rank: (index + 1) as 1 | 2 | 3,
                nickname: user.user_name,
                email: user.user_email,
                score: user.highest_score || 0,
            };
        });

        return emptySlots;
    }, [evaluationData]);

    if (isClassError || isClassLoading || !classItem) {
        return (
            <FullScreenLayout
                header={
                    <FullScreenHeader
                        title={classItem?.title || '랭킹'}
                        onBack={() => navigate(backToClassDetailPath())}
                        centerContent={renderCenterContent()}
                    />
                }
            >
                <div className="flex items-center justify-center h-full text-gray-500">
                    {isClassLoading ? '로딩 중...' : '데이터를 불러올 수 없습니다'}
                </div>
            </FullScreenLayout>
        );
    }

    return (
        <FullScreenLayout
            header={
                <FullScreenHeader
                    title={classItem.title}
                    onBack={() => navigate(backToClassDetailPath())}
                    centerContent={renderCenterContent()}
                    rightContent={renderRightContent()}
                />
            }
        >
            <div className="flex flex-1 min-h-0">
                {/* 좌측 사이드바 */}
                <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col">
                    {/* 헤더 (고정) */}
                    <div className="p-4 border-b border-gray-200">
                        <h2 className="text-lg font-bold text-gray-900">
                            랭킹 <span className="text-sm font-normal text-gray-500">(Total {scenarios.length})</span>
                        </h2>
                    </div>
                    {/* 시나리오 리스트 (스크롤) */}
                    <div className="flex-1 overflow-y-auto p-4">
                        {scenarios.length === 0 ? (
                            <div className="text-sm text-gray-500 text-center py-4">시나리오가 없습니다</div>
                        ) : (
                            <div className="space-y-1">
                                {scenarios.map((scenario) => (
                                    <button
                                        key={scenario.id}
                                        onClick={() => setSelectedScenarioId(scenario.id)}
                                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                            selectedScenarioId === scenario.id
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        <div className="font-medium">{scenario.name}</div>
                                        {scenario.practiceMode && (
                                            <div className="text-xs text-gray-500 mt-0.5">{scenario.practiceMode}</div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* 메인 영역 */}
                <div className="flex-1 bg-white overflow-y-auto">
                    <div className="max-w-[1200px] mx-auto px-12 py-8">
                        {selectedScenario && (
                            <>
                                <h1 className="text-2xl font-bold text-gray-900 mb-8">{selectedScenario.name}</h1>
                                
                                {isEvaluationLoading ? (
                                    <div className="text-center py-12 text-gray-500">
                                        랭킹 데이터를 불러오는 중...
                                    </div>
                                ) : isEvaluationError ? (
                                    <div className="text-center py-12 text-red-500">
                                        랭킹 데이터를 불러올 수 없습니다
                                    </div>
                                ) : (
                                    <Top3Winners winners={top3Winners} />
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </FullScreenLayout>
    );
}
